/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import uuid from 'uuid';
import Facebook from '@react/react-spectrum/Icon/Facebook';
import Twitter from '@react/react-spectrum/Icon/Twitter';
import YouTube from '@react/react-spectrum/Icon/YouTube';
import Instagram from '@react/react-spectrum/Icon/Instagram';
import Link from '@react/react-spectrum/Link';
import { PDPAnalytics } from '../../../../analytics';
import { PDP_SECTION, SOCIAL_LINKS } from '../../../../utils/pdpUtils/pdpEnums';
import TranslationUtil from '../../../../utils/translation';

const { getMessage } = TranslationUtil;


class StayConnected extends Component {
    onSocialLinkClick(e, socialLink) {
        const { PDPDataObject: { actionHandlerCallback } } = this.props;
        const { sectionId } = this.sectionData;
        const guid = uuid.v4();

        const actionParam = {
            actionObject: socialLink,
            guid,
        };
        actionHandlerCallback(e, sectionId, socialLink.actionId, actionParam);

        PDPAnalytics.socialLinkClick(socialLink, guid, sectionId);
    }

    get sectionData() {
        const { PDPDataObject } = this.props;
        return this.validateAndProcessParams(PDPDataObject);
    }

    validateAndProcessParams({ contentfulData }) {
	    if (!contentfulData.socialLinks || !contentfulData.socialLinks.length) {
	        return null;
	    }
	    const stayConnectedModel = {
	        sectionId: PDP_SECTION.STAY_CONNECTED,
	        socialLinkList: [],
	    };
	    // eslint-disable-next-line
	    contentfulData.socialLinks && contentfulData.socialLinks.forEach((value) => {
	        if (value.id && value.link) {
	            stayConnectedModel.socialLinkList.push({
	            actionId: value.id,
	            url: value.link && value.link.url,
	            urlData: {
	                    id: value.id,
	                    ingestId: value.ingestId,
	                    url: value.link,
	                    name: value.name,
	                },
	        });
	        }
	    });
	    return stayConnectedModel;
    }

    renderSocialIcons() {
        // socialLink.actionId should be w/o spaces or hyphens
        const { socialLinkList } = this.sectionData;
        return socialLinkList.map((socialLink) => {
            if (socialLink.actionId === undefined) {
                return null;
            }

            switch (socialLink.actionId.toLowerCase()) {
            case SOCIAL_LINKS.FACEBOOK:
                return (
                    <Link
                        tabIndex="0"
                        role="link"
                        aria-label="Facebook"
                        className="stay-connected-icon"
                        key={`social-${socialLink.actionId}`}
                        onClick={(e) => { this.onSocialLinkClick(e, socialLink); }}
                        onKeyDown={(e) => {
                            if (e.keyCode === 13) {
                                this.onSocialLinkClick(e, socialLink);
                            }
                        }}>
                        <Facebook />
                    </Link>
                );
            case SOCIAL_LINKS.TWITTER:
                return (
                    <Link
                        tabIndex="0"
                        role="link"
                        aria-label="Twitter"
                        className="stay-connected-icon"
                        key={`social-${socialLink.actionId}`}
                        onClick={(e) => { this.onSocialLinkClick(e, socialLink); }}
                        onKeyDown={(e) => {
                            if (e.keyCode === 13) {
                                this.onSocialLinkClick(e, socialLink);
                            }
                        }}>
                        <Twitter />
                    </Link>
                );
            case SOCIAL_LINKS.YOUTUBE:
                return (
                    <Link
                        tabIndex="0"
                        role="link"
                        aria-label="YouTube"
                        className="stay-connected-icon"
                        key={`social-${socialLink.actionId}`}
                        onClick={(e) => { this.onSocialLinkClick(e, socialLink); }}
                        onKeyDown={(e) => {
                            if (e.keyCode === 13) {
                                this.onSocialLinkClick(e, socialLink);
                            }
                        }}>
                        <YouTube />
                    </Link>
                );
            case SOCIAL_LINKS.INSTAGRAM:
                return (
                    <Link
                        tabIndex="0"
                        role="link"
                        aria-label="Instagram"
                        className="stay-connected-icon"
                        key={`social-${socialLink.actionId}`}
                        onClick={(e) => { this.onSocialLinkClick(e, socialLink); }}
                        onKeyDown={(e) => {
                            if (e.keyCode === 13) {
                                this.onSocialLinkClick(e, socialLink);
                            }
                        }}>
                        <Instagram />
                    </Link>
                );
            default:
                return null;
            }
        });
    }

    render() {
        const { PDPDataObject: { app: { appFamilyName } } } = this.props;
        return this.sectionData && (
            <div className="stay-connected right-panel-compt">
                <div className="stay-connected-header">
                    {getMessage('ccx.pdp.stay-connected.section-name', {
                        appname: appFamilyName,
                    })}
                </div>
                <div className="stay-connected-icons">
                    {this.renderSocialIcons()}
                </div>
            </div>
        );
    }
}

export default observer(StayConnected);
